import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import api, { uploadsUrl } from '../../utils/api';

export default function MemberVoting() {
  const { electionId } = useParams();
  const navigate = useNavigate();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [votes, setVotes] = useState({}); // { positionId: candidateMemberId }
  const [activeStep, setActiveStep] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) {
      navigate('/member-portal/login');
      return;
    }
    fetchElectionData();
  }, [electionId, navigate]);

  const fetchElectionData = async () => {
    try {
      setLoading(true);
      setError('');

      // Get election details
      const electionRes = await api.get(`/member-portal/elections`);
      const elections = electionRes.data.data || [];
      const currentElection = elections.find((e) => e.id === parseInt(electionId));

      if (!currentElection) {
        setError('Election not found or not active');
        return;
      }

      setElection(currentElection);

      // Get candidates for each position
      const positionsWithCandidates = await Promise.all(
        currentElection.positions.map(async (position) => {
          if (position.has_voted) {
            return position; // Already voted, skip fetching candidates
          }

          try {
            const candidatesRes = await api.get(
              `/member-portal/elections/${electionId}/positions/${position.id}/candidates`
            );
            return {
              ...position,
              candidates: candidatesRes.data.data || [],
            };
          } catch (err) {
            console.error(`Error fetching candidates for position ${position.id}:`, err);
            return { ...position, candidates: [] };
          }
        })
      );

      setElection({
        ...currentElection,
        positions: positionsWithCandidates,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load election data');
    } finally {
      setLoading(false);
    }
  };

  const handleVoteChange = (positionId, value) => {
    setVotes({
      ...votes,
      [positionId]: value,
    });
  };

  const handleNext = () => {
    if (activeStep < (election?.positions?.length || 0) - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSubmit = () => {
    const positionsToVote = election.positions.filter((p) => !p.has_voted);
    const allHaveChoice = positionsToVote.every((p) => votes[p.id] !== undefined && votes[p.id] !== '');

    if (!allHaveChoice) {
      setError('Please make a choice for every position before submitting.');
      return;
    }

    setConfirmDialog(true);
  };

  const confirmVotes = async () => {
    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      const positionsToVote = election.positions.filter((p) => !p.has_voted);

      await Promise.all(
        positionsToVote.map((position) => {
          const choice = votes[position.id];
          const isNo = choice === 'reject'; // No = didn't vote for (unopposed)
          return api.post(`/member-portal/elections/${electionId}/vote`, {
            position_id: position.id,
            candidate_member_id: isNo ? undefined : choice,
            vote_type: isNo ? 'reject' : undefined,
          });
        })
      );

      setSuccess('Your votes have been recorded successfully!');
      setConfirmDialog(false);

      // Refresh election data to show voted status
      setTimeout(() => {
        fetchElectionData();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit votes');
      setConfirmDialog(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!election) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error || 'Election not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/member-portal')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const isUpcoming = election.status === 'upcoming';
  const isPaused = !!election.is_paused;
  const positionsToVote = election.positions.filter((p) => !p.has_voted);
  const allVoted = election.positions.every((p) => p.has_voted);
  const currentPosition = positionsToVote[activeStep];

  if (isUpcoming) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/member-portal')} sx={{ mr: 2 }}>
            Back
          </Button>
          <Typography variant="h4">{election.name}</Typography>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <HowToVoteIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Voting Not Yet Open
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            This election opens on {new Date(election.start_date).toLocaleDateString()}. Please check back then to cast your vote.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/member-portal')}>
            Return to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  if (isPaused) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/member-portal')} sx={{ mr: 2 }}>
            Back
          </Button>
          <Typography variant="h4">{election.name}</Typography>
        </Box>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <HowToVoteIcon sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Voting Temporarily Paused
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Voting for this election has been temporarily paused by the administrator. Please check back later to cast your vote.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/member-portal')}>
            Return to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/member-portal')} sx={{ mr: 2 }}>
          Back
        </Button>
        <Typography variant="h4">{election.name}</Typography>
      </Box>

      {election.description && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body1">{election.description}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Voting Period: {new Date(election.start_date).toLocaleDateString()} -{' '}
            {new Date(election.end_date).toLocaleDateString()}
          </Typography>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {allVoted ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Thank You for Voting!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You have successfully cast your votes for all positions in this election.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/member-portal')}>
            Return to Dashboard
          </Button>
        </Paper>
      ) : (
        <>
          {positionsToVote.length > 1 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Stepper activeStep={activeStep}>
                {positionsToVote.map((position, index) => (
                  <Step key={position.id}>
                    <StepLabel>{position.name}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          )}

          {currentPosition && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <HowToVoteIcon sx={{ mr: 1, fontSize: 32 }} />
                  <Typography variant="h5">
                    {currentPosition.name}
                  </Typography>
                </Box>

                {currentPosition.candidates && currentPosition.candidates.length > 0 ? (
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">
                      {currentPosition.candidates.length === 1
                        ? 'Unopposed: vote Yes (for) or No (did not vote for)'
                        : 'Select your candidate'}
                    </FormLabel>
                    <RadioGroup
                      value={votes[currentPosition.id] || ''}
                      onChange={(e) => handleVoteChange(currentPosition.id, e.target.value)}
                    >
                      {currentPosition.candidates.map((candidate) => (
                        <Card
                          key={candidate.id}
                          sx={{
                            mb: 2,
                            border: votes[currentPosition.id] === candidate.candidate_member_id
                              ? '2px solid'
                              : '1px solid',
                            borderColor: votes[currentPosition.id] === candidate.candidate_member_id
                              ? 'primary.main'
                              : 'divider',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            '&:hover': { borderColor: 'primary.main' },
                          }}
                          onClick={() => handleVoteChange(currentPosition.id, candidate.candidate_member_id)}
                        >
                          <CardContent sx={{ p: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                              <Box
                                sx={{
                                  width: 120,
                                  minHeight: 120,
                                  flexShrink: 0,
                                  bgcolor: 'grey.100',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Avatar
                                  src={uploadsUrl(candidate.candidate_photo) || undefined}
                                  sx={{ width: 88, height: 88, bgcolor: 'primary.main' }}
                                >
                                  {!candidate.candidate_photo && (candidate.first_name?.[0] || '') + (candidate.last_name?.[0] || '')}
                                </Avatar>
                              </Box>
                              <Box sx={{ flex: 1, p: 2, display: 'flex', alignItems: 'center' }}>
                                <Radio
                                  checked={votes[currentPosition.id] === candidate.candidate_member_id}
                                  value={candidate.candidate_member_id}
                                  sx={{ mr: 2 }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="h6">
                                    {candidate.first_name} {candidate.last_name}
                                  </Typography>
                                  {candidate.manifesto && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                                      {candidate.manifesto}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                      {currentPosition.candidates.length === 1 && (
                        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                          <Chip
                            label="Yes (voted for)"
                            onClick={() => handleVoteChange(currentPosition.id, currentPosition.candidates[0].candidate_member_id)}
                            color={votes[currentPosition.id] === currentPosition.candidates[0].candidate_member_id ? 'primary' : 'default'}
                            variant={votes[currentPosition.id] === currentPosition.candidates[0].candidate_member_id ? 'filled' : 'outlined'}
                          />
                          <Chip
                            label="No (did not vote for)"
                            onClick={() => handleVoteChange(currentPosition.id, 'reject')}
                            color={votes[currentPosition.id] === 'reject' ? 'primary' : 'default'}
                            variant={votes[currentPosition.id] === 'reject' ? 'filled' : 'outlined'}
                          />
                        </Box>
                      )}
                    </RadioGroup>
                  </FormControl>
                ) : (
                  <Alert severity="info">
                    No candidates registered for this position yet.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  {activeStep < positionsToVote.length - 1 ? (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={!votes[currentPosition.id]}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!votes[currentPosition.id] || submitting}
                    >
                      {submitting ? <CircularProgress size={24} /> : 'Submit Votes'}
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Show already voted positions */}
          {election.positions.filter((p) => p.has_voted).length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Positions You've Already Voted For
              </Typography>
              <Grid container spacing={2}>
                {election.positions
                  .filter((p) => p.has_voted)
                  .map((position) => (
                    <Grid item xs={12} sm={6} md={4} key={position.id}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1">{position.name}</Typography>
                            <Chip icon={<CheckCircleIcon />} label="Voted" color="success" size="small" />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}
        </>
      )}

      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)}>
        <DialogTitle>Confirm Your Votes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please review your selections. Once submitted, you cannot change your votes.
          </Typography>
          {Object.entries(votes).map(([positionId, candidateId]) => {
            const position = election.positions.find((p) => p.id === parseInt(positionId));
            const isNo = candidateId === 'reject';
            const candidate = position?.candidates?.find(
              (c) => c.candidate_member_id === candidateId
            );
            const label = isNo
              ? 'No (did not vote for)'
              : candidate
                ? `${candidate.first_name} ${candidate.last_name}`
                : '—';
            return (
              <Box key={positionId} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {position?.name}:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button onClick={confirmVotes} variant="contained" disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Confirm & Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
